import { ExportResultCode, type Tel, type TelExportTransportFactory } from '@w/tel'
import { putMany } from './idb'
import type { RemoteTraceRecord } from './tel-read-db'

/**
 * Wraps a remote transport: every successful `sendSpans` / `sendErrorCreates`
 * upserts a `remoteTraces` row for each distinct trace id it carried.
 * `url` is the transport URL; stored markers omit its `/tel` suffix.
 * Failed sends mark nothing; other methods pass through.
 */
export function recordRemoteTraces(
  transportFactory: TelExportTransportFactory,
  opts: {
    dbName: string
    url: string
    tel: Tel
    /** Keep the newest markers by `at`. Default: 2,000. */
    maxRemoteTraces?: number
  },
): TelExportTransportFactory {
  const maxRemoteTraces = opts.maxRemoteTraces ?? 2_000
  const destination = { dbName: opts.dbName, url: opts.url.replace(/\/tel\/?$/, '') }
  return (deploy, metrics) => {
    const transport = transportFactory(deploy, metrics)
    return {
      ...transport,
      async sendSpans(spans) {
        const result = await transport.sendSpans(spans)
        // Bookkeeping only: never gates delivery of the HTTP result on local
        // IndexedDB, which can be blocked or wedged independently.
        if (result.code === ExportResultCode.SUCCESS)
          _mark(
            destination,
            maxRemoteTraces,
            spans.map((span) => span.traceId ?? span.id),
          ).catch(error => { opts.tel.error(error) })
        return result
      },
      async sendErrorCreates(errors) {
        const result = await transport.sendErrorCreates(errors)
        if (result.code === ExportResultCode.SUCCESS)
          _mark(
            destination,
            maxRemoteTraces,
            errors
              .map((error) => error.trace?.traceId ?? error.trace?.id)
              .filter((id): id is string => id !== undefined),
          ).catch(error => { opts.tel.error(error) })
        return result
      },
    }
  }
}

async function _mark(
  opts: { dbName: string; url: string },
  maxRemoteTraces: number,
  traceIds: string[],
): Promise<void> {
  const unique = [...new Set(traceIds)]
  if (unique.length === 0) return
  const at = Date.now()
  await putMany(
    opts.dbName,
    'remoteTraces',
    unique.map((traceId): RemoteTraceRecord => ({ traceId, url: opts.url, at })),
    maxRemoteTraces,
  )
}
